/**
 * "Confirm it's you": the rule and the words both apps share, so the web modal
 * and the mobile screen ask the same thing the same way (social sign-in PRD,
 * Phase 7). A change to how an account is signed in to needs a confirmation
 * from the last ten minutes; this says which proof to ask for, and how.
 *
 * The rule is to ask for one thing, the proof the account has: its password
 * when it has one, even with a provider connected too, and otherwise a fresh
 * sign-in with its provider (an account has one at most). With neither
 * usable, say it can't be confirmed rather than show an empty form. With
 * two-factor on, the password comes with a code or a recovery code.
 */

import {
  SOCIAL_PROVIDER_NAMES,
  socialSignInNotice,
} from './social-sign-in.config';
import {
  API_ERROR_CODES,
  isApiError,
  ValidationErrorClass,
} from '../types/api/errors';
import { readTwoFactorProof } from '../validation/auth.schemas';

import type { TwoFactorSignInMethod } from './two-factor.config';
import type {
  ConfirmationMethod,
  PasswordConfirmationProof,
  SocialProvider,
} from '../types/auth';

/** The step's title. */
export const CONFIRM_IDENTITY_TITLE = 'Confirm it’s you';

/** For a refusal that arrives without words of its own. */
export const COULD_NOT_CONFIRM = 'Couldn’t confirm it’s you. Try again.';

/** Said when no way to confirm can be used here. */
export const CANNOT_CONFIRM_NOTE =
  'That can’t be used here right now, so this can’t be confirmed. Try again later.';

/** What to ask for: the proof this account has, or that none can be used. */
export type ConfirmationAsk =
  | { proof: 'password'; twoFactor?: true }
  | { proof: 'provider'; provider: SocialProvider }
  | { proof: 'none'; provider?: SocialProvider };

/**
 * The step's opening line, around the change it's for ("connect Apple"),
 * which each platform sets in emphasis between `before` and `after`.
 */
export interface ConfirmationLede {
  before: string;
  after: string;
}

/**
 * What to ask for, from the 423's `methods`. `providersEnabled` is whether a
 * provider can confirm here: the social sign-in switch, on a screen that can
 * sign in with one. A phone answers per provider, since not every phone can
 * sign in with every provider. Should the API list two providers, the first
 * is asked for. `twoFactor` is the 423's: the password then comes with a code,
 * and a two-factor account has no provider to ask for instead.
 */
export function confirmationAsk(
  methods: readonly ConfirmationMethod[],
  providersEnabled: boolean | ((provider: SocialProvider) => boolean),
  { twoFactor = false }: { twoFactor?: boolean } = {}
): ConfirmationAsk {
  if (methods.includes('password')) {
    return twoFactor
      ? { proof: 'password', twoFactor: true }
      : { proof: 'password' };
  }
  const provider = methods.find(
    (method): method is SocialProvider => method !== 'password'
  );
  if (provider === undefined) return { proof: 'none' };
  const enabled =
    typeof providersEnabled === 'function'
      ? providersEnabled(provider)
      : providersEnabled;
  return enabled
    ? { proof: 'provider', provider }
    : { proof: 'none', provider };
}

/** The opening line for what's asked. */
export function confirmationLede(ask: ConfirmationAsk): ConfirmationLede {
  if (ask.proof === 'password') {
    return ask.twoFactor
      ? {
          before:
            'Enter your password and a code from your authenticator app to ',
          after: '.',
        }
      : { before: 'Enter your password to ', after: '.' };
  }
  const name = ask.provider
    ? SOCIAL_PROVIDER_NAMES[ask.provider]
    : 'Google or Apple';
  if (ask.proof === 'provider') {
    return {
      before: `You sign in to Dō with ${name}, so sign in with ${name} again to `,
      after: '. You’ll come straight back here.',
    };
  }
  return { before: 'To ', after: `, you’d sign in with ${name} again.` };
}

/** What's typed for the password proof, on the website's modal or the app's screen. */
export interface TypedPasswordProof {
  password: string;
  /** With two-factor on: the code, or a recovery code, as typed. */
  code: string;
  /** Which second factor the field asks for. */
  method: TwoFactorSignInMethod;
  /** Two-factor is on: the code comes with the password. */
  twoFactor: boolean;
}

/** Whether Confirm can be pressed: the password, and with two-factor on the code. */
export function passwordProofReady(typed: TypedPasswordProof): boolean {
  return (
    typed.password.length > 0 &&
    (!typed.twoFactor || typed.code.trim().length > 0)
  );
}

/**
 * What Confirm sends: the password, and with two-factor on the code as the
 * shared rule reads it. Or why the code isn't one yet, for under its field.
 */
export function passwordProofFrom(
  typed: TypedPasswordProof
): { proof: PasswordConfirmationProof } | { problem: string } {
  if (!typed.twoFactor) return { proof: { password: typed.password } };
  const read = readTwoFactorProof(typed.method, typed.code);
  return 'problem' in read
    ? read
    : { proof: { password: typed.password, ...read.proof } };
}

/** Every field a proof sends, which is where the API puts a refusal. */
const PROOF_FIELDS = [
  'password',
  'code',
  'recovery_code',
  'id_token',
  'identity_token',
  'nonce',
];

/** Where a refused password proof is said: the field it came under. */
export type ConfirmationRefusedField = 'password' | TwoFactorSignInMethod;

/** The first field a refusal names, in `PROOF_FIELDS` order, and its words. */
function refusedProofField(
  error: unknown
): { field: string; message: string } | undefined {
  if (!(error instanceof ValidationErrorClass)) return undefined;
  for (const field of PROOF_FIELDS) {
    const [message] = error.getFieldErrors(field);
    if (message) return { field, message };
  }
  return undefined;
}

/**
 * The field a refused password proof came under, the one
 * `confirmationErrorMessage` words. The API checks the password first, then
 * the two-factor code, so a refused code (or recovery code) means the password
 * was right, and it stays. Anything else, such as too many tries, is said on
 * the password.
 */
export function confirmationRefusedField(
  error: unknown
): ConfirmationRefusedField {
  const refused = refusedProofField(error)?.field;
  return refused === 'code' || refused === 'recovery_code'
    ? refused
    : 'password';
}

/**
 * What to say when confirming is refused: the API's words for a refused proof
 * (a 422 under the field it sent: the password, a two-factor code, or a
 * provider's token or nonce), the sign-in's pause after too many tries (six a
 * minute), and otherwise that it didn't work.
 */
export function confirmationErrorMessage(error: unknown): string {
  const refused = refusedProofField(error);
  if (refused) return refused.message;
  if (isApiError(error) && error.code === API_ERROR_CODES.RATE_LIMITED) {
    const { title, body } = socialSignInNotice('rate_limited');
    return title ? `${title}. ${body}` : body;
  }
  return COULD_NOT_CONFIRM;
}
